import App from "components/App/App";
import ErrorBoundary from "components/Common/ErrorBoundary";
import SwappingIntlProvider from "components/UI/SwappingIntlProvider/SwappingIntlProvider";
import React from "react";
import { createRoot } from "react-dom/client";
import { initSentryLazy } from "api/sentry";
import { Route, Router } from "react-router-dom";
import history from "router/history";
import "./index.css";
import firebase from "api/firebase/firebase";
import loadGoogleMaps from "api/GoogleMaps";
import { isMapLessPath } from "utils/firstMapView";
import { prefetchDashboard } from "components/App/dashboardLoader";
import { installStaleChunkReload } from "utils/staleChunkReload";
import { skipsMapPreload } from "utils/landingHint";
import { unregister } from "./registerServiceWorker";

// Reload once when a stale deploy serves index.html for a missing chunk.
installStaleChunkReload();

firebase.init();

// Start loading the Google Maps JS API now, in parallel with sign-in, config
// and the Dashboard chunk, instead of when the map mounts. The key is a
// build-time env var and no language is passed, so nothing here waits on
// runtime state. GoogleMapsEngine.init() awaits the same cached promise. Pages
// that never show a map skip it.
// A browser last shown the full landing page while signed out skips both
// preloads on the sign-in paths: that page has no map (utils/landingHint).
const preloadMap = !skipsMapPreload(window.location.pathname);

if (preloadMap && !isMapLessPath(window.location.pathname)) {
  loadGoogleMaps().catch((error) =>
    // The map retries when it mounts (the loader forgets failed loads).
    console.error("Google Maps preload failed", error),
  );
}

// Start downloading the Dashboard now, so it overlaps the Login chunk and
// the sign-in instead of waiting for them. Only on pages that show it.
if (preloadMap) prefetchDashboard();

// Initialize Sentry lazily: early errors are buffered and the SDK loads after
// the first map view (utils/firstMapView).
initSentryLazy();

const app = (
  <ErrorBoundary>
    <SwappingIntlProvider>
      <Router history={history}>
        <Route>
          <App />
        </Route>
      </Router>
    </SwappingIntlProvider>
  </ErrorBoundary>
);

const root = createRoot(document.getElementById("root"));
root.render(app);

unregister();
